import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import CardLayoutTheme from '../theme/CardLayoutTheme';
import api from '../api/api';
import { ThemeContext } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import MenuItemImageUpload from '../components/MenuItemImageUpload';
import { useImageUpload } from '../hooks/useImageUpload';
import { IMAGE_GATEWAY_URL } from '../config/images';

interface TaxConfig {
  id?: number;
  gstin?: string;
  businessName?: string;
  taxEnabled: boolean;
  taxType: 'INTRA_STATE' | 'INTER_STATE';
  cgstRate: number;
  sgstRate: number;
  igstRate: number;
  serviceChargeEnabled: boolean;
  serviceChargeRate: number;
  // UK VAT fields (GB vendors)
  vatNumber?: string;
  vatRate?: number;
  serviceChargeType?: 'DISCRETIONARY' | 'MANDATORY';
  // Business contact fields for public page
  address?: string;
  phone?: string;
  cuisineTypes?: string;
  operatingHours?: string;
  tagline?: string;
  // Order settings
  allowGuestTableOrders?: boolean;
  // Branding images
  logoImageKey?: string;
  coverImageKey?: string;
  coverImageUrl?: string;
}

const VendorTaxConfiguration: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext);
  const { user } = useAuth();
  const { deleteImage } = useImageUpload();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [taxConfig, setTaxConfig] = useState<TaxConfig>({
    taxEnabled: false,
    taxType: 'INTRA_STATE',
    cgstRate: 2.5,
    sgstRate: 2.5,
    igstRate: 5.0,
    serviceChargeEnabled: false,
    serviceChargeRate: 10.0,
  });

  // Branding image state
  const [logoImageKey, setLogoImageKey] = useState<string | null>(null);
  const [logoImageUrl, setLogoImageUrl] = useState<string | null>(null);
  const [coverImageKey, setCoverImageKey] = useState<string | null>(null);
  const [coverImageUrl, setCoverImageUrl] = useState<string | null>(null);

  const handleLogoUploaded = useCallback(async (newKey: string) => {
    if (logoImageKey && logoImageKey !== newKey) {
      await deleteImage(logoImageKey);
    }
    setLogoImageKey(newKey);
    setLogoImageUrl(`${IMAGE_GATEWAY_URL}/${newKey}`);
    setTaxConfig(prev => ({ ...prev, logoImageKey: newKey }));
  }, [logoImageKey, deleteImage]);

  const handleLogoRemoved = useCallback(() => {
    setLogoImageKey(null);
    setLogoImageUrl(null);
    setTaxConfig(prev => ({ ...prev, logoImageKey: undefined }));
  }, []);

  const handleCoverUploaded = useCallback(async (newKey: string) => {
    if (coverImageKey && coverImageKey !== newKey) {
      await deleteImage(coverImageKey);
    }
    setCoverImageKey(newKey);
    setCoverImageUrl(`${IMAGE_GATEWAY_URL}/${newKey}`);
    setTaxConfig(prev => ({ ...prev, coverImageKey: newKey }));
  }, [coverImageKey, deleteImage]);

  const handleCoverRemoved = useCallback(() => {
    setCoverImageKey(null);
    setCoverImageUrl(null);
    setTaxConfig(prev => ({ ...prev, coverImageKey: undefined }));
  }, []);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [successMessage, setSuccessMessage] = useState('');
  const [preview, setPreview] = useState<{
    subtotal: number;
    cgst: number;
    sgst: number;
    igst: number;
    vat: number;
    serviceCharge: number;
    total: number;
  } | null>(null);

  // Country-aware form (UK plan Phase 2 step 6): GB vendors configure a VAT
  // number and single VAT rate instead of GSTIN/CGST/SGST, prices are
  // VAT-inclusive, and the service charge carries a DISCRETIONARY/MANDATORY type.
  const [countryInfo, setCountryInfo] = useState<{
    country: string;
    currencySymbol: string;
    taxLabel: string;
  } | null>(null);
  const isVatCountry = countryInfo?.taxLabel === 'VAT';
  const currencySymbol = countryInfo?.currencySymbol || '₹';

  useEffect(() => {
    fetchTaxConfig();
    api.get('/api/vendor/config/country')
      .then((res) => setCountryInfo(res.data))
      .catch((err) => console.error('Error fetching country config:', err));
  }, []);

  useEffect(() => {
    // Recompute preview on every config change. calculatePreview already
    // zeros tax components when taxEnabled is false, and the preview block
    // itself only renders when tax or service charge is enabled.
    calculatePreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxConfig, countryInfo]);

  const fetchTaxConfig = async () => {
    try {
      const response = await api.get('/api/vendor/tax-config');
      setTaxConfig(response.data);
      // Initialize branding image state from fetched config
      if (response.data.logoImageKey) {
        setLogoImageKey(response.data.logoImageKey);
        setLogoImageUrl(`${IMAGE_GATEWAY_URL}/${response.data.logoImageKey}`);
      }
      if (response.data.coverImageKey) {
        setCoverImageKey(response.data.coverImageKey);
        setCoverImageUrl(`${IMAGE_GATEWAY_URL}/${response.data.coverImageKey}`);
      } else if (response.data.coverImageUrl) {
        // Legacy cover image URL support
        setCoverImageUrl(response.data.coverImageUrl);
      }
    } catch (error) {
      console.error('Error fetching tax config:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculatePreview = () => {
    const subtotal = 100; // Example 100-unit order
    let cgst = 0, sgst = 0, igst = 0, vat = 0, serviceCharge = 0;

    if (taxConfig.serviceChargeEnabled) {
      serviceCharge = subtotal * (taxConfig.serviceChargeRate / 100);
    }

    let total;
    if (isVatCountry) {
      // Tax-INCLUSIVE: VAT is backed out of the gross, never added on top.
      const rate = taxConfig.vatRate ?? 20;
      if (taxConfig.taxEnabled) {
        vat = subtotal * (rate / (100 + rate));
        if (taxConfig.serviceChargeType === 'MANDATORY') {
          vat += serviceCharge * (rate / (100 + rate));
        }
      }
      total = subtotal + serviceCharge;
    } else {
      if (taxConfig.taxEnabled) {
        if (taxConfig.taxType === 'INTER_STATE') {
          igst = subtotal * (taxConfig.igstRate / 100);
        } else {
          cgst = subtotal * (taxConfig.cgstRate / 100);
          sgst = subtotal * (taxConfig.sgstRate / 100);
        }
      }
      total = subtotal + cgst + sgst + igst + serviceCharge;
    }

    setPreview({
      subtotal,
      cgst,
      sgst,
      igst,
      vat,
      serviceCharge,
      total,
    });
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (isVatCountry) {
      // Tax-ID validation branches by country (UK plan Phase 2 step 6)
      if (taxConfig.vatNumber && taxConfig.vatNumber.length > 0
          && !/^GB\d{9}(\d{3})?$/.test(taxConfig.vatNumber)) {
        newErrors.vatNumber = 'Invalid VAT number format. Expected: GB123456789';
      }
      if (taxConfig.vatRate !== undefined && taxConfig.vatRate !== null
          && (taxConfig.vatRate < 0 || taxConfig.vatRate > 100)) {
        newErrors.vatRate = 'VAT rate must be between 0 and 100';
      }
    } else {
      if (taxConfig.gstin && taxConfig.gstin.length > 0) {
        if (taxConfig.gstin.length !== 15) {
          newErrors.gstin = 'GSTIN must be exactly 15 characters';
        } else if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/.test(taxConfig.gstin)) {
          newErrors.gstin = 'Invalid GSTIN format';
        }
      }

      if (taxConfig.cgstRate < 0 || taxConfig.cgstRate > 100) {
        newErrors.cgstRate = 'CGST rate must be between 0 and 100';
      }

      if (taxConfig.sgstRate < 0 || taxConfig.sgstRate > 100) {
        newErrors.sgstRate = 'SGST rate must be between 0 and 100';
      }

      if (taxConfig.igstRate < 0 || taxConfig.igstRate > 100) {
        newErrors.igstRate = 'IGST rate must be between 0 and 100';
      }
    }

    if (taxConfig.serviceChargeRate < 0 || taxConfig.serviceChargeRate > 100) {
      newErrors.serviceChargeRate = 'Service charge rate must be between 0 and 100';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    setSuccessMessage('');

    try {
      await api.post('/api/vendor/tax-config', taxConfig);
      setSuccessMessage('Tax configuration saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error: any) {
      console.error('Error saving tax config:', error);
      setErrors({ submit: error.response?.data?.message || 'Failed to save tax configuration' });
    } finally {
      setSaving(false);
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="text-center py-8">
          <p style={{ color: theme.gray[500] }}>Loading tax configuration...</p>
        </div>
      );
    }

    return (
      <div className="max-w-4xl mx-auto p-4 sm:p-6">
        {/* Success Message */}
        {successMessage && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-800 rounded-lg text-sm">
            {successMessage}
          </div>
        )}

        {/* Error Message */}
        {errors.submit && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-800 rounded-lg text-sm">
            {errors.submit}
          </div>
        )}

        {/* Tax Configuration Form */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border p-6 mb-4" style={{ borderColor: theme.gray[200] }}>
          {/* Enable Tax Calculation */}
          <div className="mb-6">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={taxConfig.taxEnabled}
                onChange={(e) => setTaxConfig({ ...taxConfig, taxEnabled: e.target.checked })}
                className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
              />
              <span className="ml-3 text-base font-semibold" style={{ color: theme.text }}>
                Enable Tax Calculation
              </span>
            </label>
          </div>

          {taxConfig.taxEnabled && (
            <>
              {/* Tax ID (GSTIN / VAT number by country) and Business Name */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {isVatCountry ? (
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: theme.gray[700] }}>
                      VAT Number (Optional)
                    </label>
                    <input
                      type="text"
                      value={taxConfig.vatNumber || ''}
                      onChange={(e) => setTaxConfig({ ...taxConfig, vatNumber: e.target.value.toUpperCase().replace(/\s/g, '') })}
                      placeholder="GB123456789"
                      maxLength={14}
                      className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                        errors.vatNumber ? 'border-red-500' : 'border-gray-300'
                      }`}
                      style={{ backgroundColor: theme.background, color: theme.text }}
                    />
                    <p className="mt-1 text-xs" style={{ color: theme.gray[500] }}>
                      Leave empty if not VAT-registered — no VAT lines will appear on receipts.
                    </p>
                    {errors.vatNumber && (
                      <p className="mt-1 text-xs text-red-600">{errors.vatNumber}</p>
                    )}
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: theme.gray[700] }}>
                      GSTIN (Optional)
                    </label>
                    <input
                      type="text"
                      value={taxConfig.gstin || ''}
                      onChange={(e) => setTaxConfig({ ...taxConfig, gstin: e.target.value.toUpperCase() })}
                      placeholder="22AAAAA0000A1Z5"
                      maxLength={15}
                      className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                        errors.gstin ? 'border-red-500' : 'border-gray-300'
                      }`}
                      style={{ backgroundColor: theme.background, color: theme.text }}
                    />
                    {errors.gstin && (
                      <p className="mt-1 text-xs text-red-600">{errors.gstin}</p>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: theme.gray[700] }}>
                    Business Name (Optional)
                  </label>
                  <input
                    type="text"
                    value={taxConfig.businessName || ''}
                    onChange={(e) => setTaxConfig({ ...taxConfig, businessName: e.target.value })}
                    placeholder="Your Business Name"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    style={{ backgroundColor: theme.background, color: theme.text }}
                  />
                </div>
              </div>

              {/* Business Contact Information for Public Page */}
              <div className="border-t pt-6 mb-6" style={{ borderColor: theme.gray[200] }}>
                <h4 className="text-base font-semibold mb-4" style={{ color: theme.text }}>
                  Public Website Information
                </h4>
                <p className="text-xs mb-4" style={{ color: theme.gray[500] }}>
                  This information will be displayed on your public vendor page at /v/{'{vendorId}'}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: theme.gray[700] }}>
                      Phone Number
                    </label>
                    <input
                      type="text"
                      value={taxConfig.phone || ''}
                      onChange={(e) => setTaxConfig({ ...taxConfig, phone: e.target.value })}
                      placeholder="9876543210"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      style={{ backgroundColor: theme.background, color: theme.text }}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: theme.gray[700] }}>
                      Cuisine Types
                    </label>
                    <input
                      type="text"
                      value={taxConfig.cuisineTypes || ''}
                      onChange={(e) => setTaxConfig({ ...taxConfig, cuisineTypes: e.target.value })}
                      placeholder="Indian, Chinese, Mughlai"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      style={{ backgroundColor: theme.background, color: theme.text }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: theme.gray[700] }}>
                      Operating Hours
                    </label>
                    <input
                      type="text"
                      value={taxConfig.operatingHours || ''}
                      onChange={(e) => setTaxConfig({ ...taxConfig, operatingHours: e.target.value })}
                      placeholder="11:00 AM - 11:00 PM"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      style={{ backgroundColor: theme.background, color: theme.text }}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: theme.gray[700] }}>
                      Tagline / Slogan
                    </label>
                    <input
                      type="text"
                      value={taxConfig.tagline || ''}
                      onChange={(e) => setTaxConfig({ ...taxConfig, tagline: e.target.value })}
                      placeholder="Delicious Food, Great Service"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      style={{ backgroundColor: theme.background, color: theme.text }}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: theme.gray[700] }}>
                    Address
                  </label>
                  <textarea
                    value={taxConfig.address || ''}
                    onChange={(e) => setTaxConfig({ ...taxConfig, address: e.target.value })}
                    placeholder="123 Main Street, City, State, PIN Code"
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    style={{ backgroundColor: theme.background, color: theme.text }}
                  />
                </div>

                {/* Branding Images */}
                {user?.id && (
                  <div className="border-t pt-6 mt-4" style={{ borderColor: theme.gray[200] }}>
                    <h4 className="text-base font-semibold mb-1" style={{ color: theme.text }}>
                      Branding Images
                    </h4>
                    <p className="text-xs mb-4" style={{ color: theme.gray[500] }}>
                      Upload your restaurant logo and cover image for your public page.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: theme.gray[700] }}>
                          Logo
                        </label>
                        <MenuItemImageUpload
                          vendorId={user.id}
                          type="logo"
                          entityId="brand"
                          imageUrl={logoImageUrl}
                          imageKey={logoImageKey}
                          onImageUploaded={handleLogoUploaded}
                          onImageRemoved={handleLogoRemoved}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2" style={{ color: theme.gray[700] }}>
                          Cover Image
                        </label>
                        <MenuItemImageUpload
                          vendorId={user.id}
                          type="cover"
                          entityId="brand"
                          imageUrl={coverImageUrl}
                          imageKey={coverImageKey}
                          onImageUploaded={handleCoverUploaded}
                          onImageRemoved={handleCoverRemoved}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Order Settings */}
              <div className="border-t pt-6 mb-6" style={{ borderColor: theme.gray[200] }}>
                <h4 className="text-base font-semibold mb-4" style={{ color: theme.text }}>
                  Order Settings
                </h4>

                <div className="flex items-center justify-between p-4 rounded-lg" style={{ backgroundColor: theme.gray[50] }}>
                  <div>
                    <label className="text-sm font-medium" style={{ color: theme.text }}>
                      Allow Guest Orders from Tables
                    </label>
                    <p className="text-xs mt-1" style={{ color: theme.gray[500] }}>
                      When enabled, customers can place orders from tables without logging in. Orders will be marked as "Table Order" instead of showing customer name.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer ml-4">
                    <input
                      type="checkbox"
                      checked={taxConfig.allowGuestTableOrders !== false}
                      onChange={(e) => setTaxConfig({ ...taxConfig, allowGuestTableOrders: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>

              {/* VAT rate (GB) — prices are VAT-inclusive, single rate */}
              {isVatCountry && (
                <div className="mb-6 w-full md:w-1/3">
                  <label className="block text-sm font-medium mb-2" style={{ color: theme.gray[700] }}>
                    VAT Rate (%)
                  </label>
                  <input
                    type="number"
                    value={taxConfig.vatRate ?? ''}
                    onChange={(e) => setTaxConfig({
                      ...taxConfig,
                      vatRate: e.target.value === '' ? undefined : parseFloat(e.target.value),
                    })}
                    placeholder="20 (standard rate)"
                    min="0"
                    max="100"
                    step="0.01"
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                      errors.vatRate ? 'border-red-500' : 'border-gray-300'
                    }`}
                    style={{ backgroundColor: theme.background, color: theme.text }}
                  />
                  <p className="mt-1 text-xs" style={{ color: theme.gray[500] }}>
                    Leave empty to use the standard 20% rate. Menu prices are VAT-inclusive — VAT is shown on the bill, never added on top.
                  </p>
                  {errors.vatRate && (
                    <p className="mt-1 text-xs text-red-600">{errors.vatRate}</p>
                  )}
                </div>
              )}

              {/* Tax Type Selection (IN only) */}
              {!isVatCountry && (
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2" style={{ color: theme.gray[700] }}>
                  Tax Type
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      value="INTRA_STATE"
                      checked={taxConfig.taxType === 'INTRA_STATE'}
                      onChange={(e) => setTaxConfig({ ...taxConfig, taxType: 'INTRA_STATE' })}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm" style={{ color: theme.text }}>
                      Intra-State (CGST + SGST)
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      value="INTER_STATE"
                      checked={taxConfig.taxType === 'INTER_STATE'}
                      onChange={(e) => setTaxConfig({ ...taxConfig, taxType: 'INTER_STATE' })}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm" style={{ color: theme.text }}>
                      Inter-State (IGST)
                    </span>
                  </label>
                </div>
              </div>
              )}

              {/* GST Rates (IN only) */}
              {!isVatCountry && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {taxConfig.taxType === 'INTRA_STATE' ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: theme.gray[700] }}>
                        CGST Rate (%)
                      </label>
                      <input
                        type="number"
                        value={taxConfig.cgstRate}
                        onChange={(e) => setTaxConfig({ ...taxConfig, cgstRate: parseFloat(e.target.value) || 0 })}
                        min="0"
                        max="100"
                        step="0.01"
                        className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                          errors.cgstRate ? 'border-red-500' : 'border-gray-300'
                        }`}
                        style={{ backgroundColor: theme.background, color: theme.text }}
                      />
                      {errors.cgstRate && (
                        <p className="mt-1 text-xs text-red-600">{errors.cgstRate}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: theme.gray[700] }}>
                        SGST Rate (%)
                      </label>
                      <input
                        type="number"
                        value={taxConfig.sgstRate}
                        onChange={(e) => setTaxConfig({ ...taxConfig, sgstRate: parseFloat(e.target.value) || 0 })}
                        min="0"
                        max="100"
                        step="0.01"
                        className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                          errors.sgstRate ? 'border-red-500' : 'border-gray-300'
                        }`}
                        style={{ backgroundColor: theme.background, color: theme.text }}
                      />
                      {errors.sgstRate && (
                        <p className="mt-1 text-xs text-red-600">{errors.sgstRate}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: theme.gray[700] }}>
                        Total GST
                      </label>
                      <div className="px-3 py-2 text-sm rounded-lg border border-gray-200" style={{ backgroundColor: theme.gray[50], color: theme.text }}>
                        {(taxConfig.cgstRate + taxConfig.sgstRate).toFixed(2)}%
                      </div>
                    </div>
                  </>
                ) : (
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: theme.gray[700] }}>
                      IGST Rate (%)
                    </label>
                    <input
                      type="number"
                      value={taxConfig.igstRate}
                      onChange={(e) => setTaxConfig({ ...taxConfig, igstRate: parseFloat(e.target.value) || 0 })}
                      min="0"
                      max="100"
                      step="0.01"
                      className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                        errors.igstRate ? 'border-red-500' : 'border-gray-300'
                      }`}
                      style={{ backgroundColor: theme.background, color: theme.text }}
                    />
                    {errors.igstRate && (
                      <p className="mt-1 text-xs text-red-600">{errors.igstRate}</p>
                    )}
                  </div>
                )}
              </div>
              )}
            </>
          )}

          {/* Service Charge */}
          <div className="border-t pt-6 mb-6" style={{ borderColor: theme.gray[200] }}>
            <label className="flex items-center cursor-pointer mb-4">
              <input
                type="checkbox"
                checked={taxConfig.serviceChargeEnabled}
                onChange={(e) => setTaxConfig({ ...taxConfig, serviceChargeEnabled: e.target.checked })}
                className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
              />
              <span className="ml-3 text-base font-semibold" style={{ color: theme.text }}>
                Enable Service Charge
              </span>
            </label>

            {taxConfig.serviceChargeEnabled && (
              <>
              <div className="w-full md:w-1/3">
                <label className="block text-sm font-medium mb-2" style={{ color: theme.gray[700] }}>
                  Service Charge Rate (%)
                </label>
                <input
                  type="number"
                  value={taxConfig.serviceChargeRate}
                  onChange={(e) => setTaxConfig({ ...taxConfig, serviceChargeRate: parseFloat(e.target.value) || 0 })}
                  min="0"
                  max="100"
                  step="0.01"
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                    errors.serviceChargeRate ? 'border-red-500' : 'border-gray-300'
                  }`}
                  style={{ backgroundColor: theme.background, color: theme.text }}
                />
                {errors.serviceChargeRate && (
                  <p className="mt-1 text-xs text-red-600">{errors.serviceChargeRate}</p>
                )}
              </div>

              {/* GB service charge treatment: discretionary carries no VAT,
                  mandatory is part of the meal price and carries standard-rate VAT */}
              {isVatCountry && (
                <div className="mt-4">
                  <label className="block text-sm font-medium mb-2" style={{ color: theme.gray[700] }}>
                    Service Charge Type
                  </label>
                  <div className="flex space-x-4">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        value="DISCRETIONARY"
                        checked={(taxConfig.serviceChargeType || 'DISCRETIONARY') === 'DISCRETIONARY'}
                        onChange={() => setTaxConfig({ ...taxConfig, serviceChargeType: 'DISCRETIONARY' })}
                        className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm" style={{ color: theme.text }}>
                        Discretionary (customer can decline — no VAT)
                      </span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        value="MANDATORY"
                        checked={taxConfig.serviceChargeType === 'MANDATORY'}
                        onChange={() => setTaxConfig({ ...taxConfig, serviceChargeType: 'MANDATORY' })}
                        className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm" style={{ color: theme.text }}>
                        Mandatory (carries VAT)
                      </span>
                    </label>
                  </div>
                  <p className="mt-1 text-xs" style={{ color: theme.gray[500] }}>
                    Must be labelled correctly on the menu and bill. All tips and service charges must be passed to workers (Employment (Allocation of Tips) Act 2023).
                  </p>
                </div>
              )}
              </>
            )}
          </div>

          {/* Save Button */}
          <div className="flex justify-end border-t pt-4" style={{ borderColor: theme.gray[200] }}>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-10 py-3 text-base font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: saving ? `${theme.accent}66` : `${theme.accent}22`,
                border: `1.5px solid ${theme.accent}`,
                color: theme.accent,
                fontWeight: 600,
                cursor: saving ? 'not-allowed' : 'pointer',
                minWidth: '200px'
              }}
              onMouseEnter={(e) => {
                if (!saving) {
                  e.currentTarget.style.backgroundColor = `${theme.accent}44`;
                }
              }}
              onMouseLeave={(e) => {
                if (!saving) {
                  e.currentTarget.style.backgroundColor = `${theme.accent}22`;
                }
              }}
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>
          </div>
        </div>

        {/* Preview Section */}
        {(taxConfig.taxEnabled || taxConfig.serviceChargeEnabled) && preview && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border p-6" style={{ borderColor: theme.gray[200] }}>
            <h3 className="text-base font-bold mb-4" style={{ color: theme.primary }}>
              Invoice Preview ({currencySymbol}100 Order)
            </h3>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span style={{ color: theme.gray[700] }}>Subtotal:</span>
                <span className="font-medium" style={{ color: theme.text }}>{currencySymbol}{preview.subtotal.toFixed(2)}</span>
              </div>

              {taxConfig.taxEnabled && !isVatCountry && (
                <>
                  {taxConfig.taxType === 'INTRA_STATE' ? (
                    <>
                      <div className="flex justify-between">
                        <span style={{ color: theme.gray[700] }}>CGST ({taxConfig.cgstRate}%):</span>
                        <span className="font-medium" style={{ color: theme.text }}>{currencySymbol}{preview.cgst.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span style={{ color: theme.gray[700] }}>SGST ({taxConfig.sgstRate}%):</span>
                        <span className="font-medium" style={{ color: theme.text }}>{currencySymbol}{preview.sgst.toFixed(2)}</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between">
                      <span style={{ color: theme.gray[700] }}>IGST ({taxConfig.igstRate}%):</span>
                      <span className="font-medium" style={{ color: theme.text }}>{currencySymbol}{preview.igst.toFixed(2)}</span>
                    </div>
                  )}
                </>
              )}

              {taxConfig.serviceChargeEnabled && (
                <div className="flex justify-between">
                  <span style={{ color: theme.gray[700] }}>Service Charge ({taxConfig.serviceChargeRate}%):</span>
                  <span className="font-medium" style={{ color: theme.text }}>{currencySymbol}{preview.serviceCharge.toFixed(2)}</span>
                </div>
              )}

              <div className="flex justify-between pt-3 mt-3 border-t font-semibold" style={{ borderColor: theme.gray[200] }}>
                <span style={{ color: theme.text }}>Total:</span>
                <span style={{ color: theme.primary }}>{currencySymbol}{preview.total.toFixed(2)}</span>
              </div>

              {isVatCountry && taxConfig.taxEnabled && preview.vat > 0 && (
                <div className="flex justify-between text-xs">
                  <span style={{ color: theme.gray[500] }}>Includes VAT ({taxConfig.vatRate ?? 20}%):</span>
                  <span style={{ color: theme.gray[500] }}>{currencySymbol}{preview.vat.toFixed(2)}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <CardLayoutTheme
      title="Tax Configuration"
      categories={[{ id: 1, name: 'Tax Settings' }]}
      renderItems={renderContent}
      hideSidebar={true}
      sidebarTitle="Tax Configuration"
    />
  );
};

export default VendorTaxConfiguration;